import { useState, useMemo, useRef, useEffect } from "react";
import { formatContactDisplayName } from "@domaddress/shared";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import {
  parseBezoekerFormEmail,
  type ParsedBezoekerForm,
} from "../lib/parseBezoekerFormEmail";

function isPlausibleEmail(s: string | undefined): boolean {
  if (!s?.trim()) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
}

/**
 * Paste notification e-mails from the website visitor form and import into Bezoekersbestand.
 */
export default function ImportBezoekersPage() {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [raw, setRaw] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  /** Hide the “import gelukt” line when the user starts a new paste. */
  useEffect(() => {
    if (successMessage && raw.trim().length > 0) {
      setSuccessMessage(null);
    }
  }, [raw, successMessage]);

  const results = useMemo(() => {
    if (!raw.trim()) return [];
    return parseBezoekerFormEmail(raw);
  }, [raw]);

  const readyRows = useMemo(() => {
    const out: ParsedBezoekerForm[] = [];
    for (const r of results) {
      if (!r.ok) continue;
      if (!isPlausibleEmail(r.data.email)) continue;
      out.push(r.data);
    }
    return out;
  }, [results]);

  const problemCount = results.filter((r) => !r.ok).length;
  const emailProblemCount = results.filter(
    (r) => r.ok && !isPlausibleEmail(r.data.email)
  ).length;

  async function handleImport() {
    if (readyRows.length === 0) {
      setError("Geen geldige regels om te importeren.");
      return;
    }
    const count = readyRows.length;
    setSaving(true);
    setError(null);
    try {
      await api.post("/api/bezoekersbestand/import", { entries: readyRows });
      setRaw("");
      setSuccessMessage(
        count === 1
          ? "1 dominicusganger geïmporteerd. Je kunt hieronder de volgende e-mail plakken."
          : `${count} dominicusgangers geïmporteerd. Je kunt hieronder de volgende e-mail plakken.`
      );
      textareaRef.current?.focus();
    } catch (err: unknown) {
      const ax = err as {
        response?: { data?: { error?: string; details?: { fieldErrors?: Record<string, string[]> } } };
      };
      const data = ax.response?.data;
      const fromFields = data?.details?.fieldErrors
        ? Object.entries(data.details.fieldErrors)
            .flatMap(([, msgs]) => msgs ?? [])
            .filter(Boolean)[0]
        : undefined;
      setError(data?.error ?? fromFields ?? "Importeren mislukt");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <Link to="/dominicusgangers" className="btn-back">
            ← Terug naar Bezoekersbestand
          </Link>
          <h2 className="page-title">Importeren uit e-mail (websiteformulier)</h2>
          <p className="page-subtitle">
            Plak de volledige tekst van een melding zoals &quot;Er is een nieuw bezoekersformulier ingevuld&quot;.
            Je kunt meerdere formulieren in één keer plakken als de koptekst herhaald wordt.
          </p>
        </div>
      </div>

      <div className="detail-section">
        <label className="section-title" htmlFor="bezoeker-paste" style={{ display: "block" }}>
          Geplakte tekst
        </label>
        {successMessage && (
          <p className="import-success-message" role="status">
            {successMessage}
          </p>
        )}
        <textarea
          ref={textareaRef}
          id="bezoeker-paste"
          className="import-paste-area"
          rows={16}
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          placeholder={`Er is een nieuw bezoekersformulier ingevuld:

Naam: …
Achternaam: …

Geboortedatum: …

Straat: …
Postcode: …
Woonplaats: …

Telefoonnummer: …

Email-adres: …

Wil nieuwsbrief: Ingestemd: …
Wil informatie over activiteiten: Ingestemd: …
Bereid tot vrijwilligerswerk: Niet ingestemd: …`}
        />
      </div>

      {raw.trim() && (
        <div className="detail-section" style={{ marginTop: "1.25rem" }}>
          <h3 className="section-title">Controle</h3>
          {results.length === 0 ? (
            <p className="page-subtitle">Geen blokken herkend. Controleer de tekst.</p>
          ) : (
            <ul className="import-preview-list">
              {results.map((r, i) => {
                if (!r.ok) {
                  return (
                    <li key={i} className="import-preview-item import-preview-item--err">
                      <strong>Blok {i + 1}</strong> — {r.error}
                    </li>
                  );
                }
                const badEmail = !isPlausibleEmail(r.data.email);
                return (
                  <li
                    key={i}
                    className={`import-preview-item ${badEmail ? "import-preview-item--warn" : ""}`}
                  >
                    <strong>
                      {formatContactDisplayName(r.data)}
                    </strong>
                    {r.data.birth_date && (
                      <span className="page-subtitle" style={{ marginLeft: "8px" }}>
                        (geb. {r.data.birth_date})
                      </span>
                    )}
                    {r.data.email && (
                      <span style={{ marginLeft: "8px" }}>{r.data.email}</span>
                    )}
                    {badEmail && (
                      <span style={{ color: "var(--danger)", marginLeft: "8px" }}>
                        Ongeldig e-mailadres — regel overgeslagen bij import.
                      </span>
                    )}
                    <div className="page-subtitle" style={{ marginTop: "6px" }}>
                      Nieuwsbrief: {r.data.wants_newsletter ? "ja" : "nee"} · Activiteiten:{" "}
                      {r.data.wants_activities_info ? "ja" : "nee"} · Vrijwilliger:{" "}
                      {r.data.willing_volunteer ? "ja" : "nee"}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {(problemCount > 0 || emailProblemCount > 0) && (
            <p className="form-error" style={{ marginTop: "12px" }}>
              {problemCount > 0 &&
                `${problemCount} blok(ken) niet herkend. `}
              {emailProblemCount > 0 &&
                `${emailProblemCount} regel(s) met ongeldig e-mail worden overgeslagen.`}
            </p>
          )}
        </div>
      )}

      {error && <p className="form-error">{error}</p>}

      <div style={{ display: "flex", gap: "12px", marginTop: "24px", flexWrap: "wrap" }}>
        <button
          type="button"
          className="btn-primary"
          disabled={saving || readyRows.length === 0}
          onClick={handleImport}
        >
          {saving ? "Bezig…" : `Importeer ${readyRows.length} in Bezoekersbestand`}
        </button>
        <Link to="/dominicusgangers" className="btn-secondary">
          Annuleren
        </Link>
      </div>
    </div>
  );
}
